// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { RawBuilder, sql } from "kysely";

import { queryBuilder } from "~/api/materialize";
import { buildSubscribeQuery } from "~/api/materialize/buildSubscribeQuery";

// These read a replica's introspection relations, so they must run in
// a session pinned to that replica (`cluster` and `cluster_replica`). They then
// run on that cluster, not on `mz_catalog_server`.
//
// A dataflow's root operator (an address of length one) accounts for all the
// time spent in the regions and operators it contains, so summing roots counts
// each nanosecond once. Summing every operator would count it once per level
// of nesting.

/** The unit the subscribes round elapsed time down to, in nanoseconds. */
export const ELAPSED_UNIT_NS = 10_000_000;

// Every dataflow's counter grows each second, idle or not, so unrounded these
// send an update per dataflow and worker each second. In 10 ms units an idle
// dataflow's value stops changing and the subscribe sends nothing for it:
// measured 22 rows/s unrounded against 1.2 on an idle 11-dataflow, 2-worker
// replica. A difference of two values is then off by under 10 ms per
// dataflow, 0.02% of a core over the minute a measurement spans at least, and
// a dataflow that runs under 10 ms in all reads as idle. 100 ms would be 2% of
// what two workers 5% busy spend in a minute.
const roundedElapsedNs = (elapsedNs: RawBuilder<unknown>) =>
  sql<
    number | null
  >`(floor(${elapsedNs} / ${sql.lit(ELAPSED_UNIT_NS)}) * ${sql.lit(ELAPSED_UNIT_NS)})::float8`;

const dataflowIdOfRoot = sql`${sql.ref("a.address")}[1]`;
const isRootOperator = sql<boolean>`list_length(${sql.ref("a.address")}) = 1`;

/** One dataflow's cumulative time on one worker. */
export interface DataflowWorkerCpuRow {
  dataflowId: string;
  workerId: number;
  elapsedNs: number;
}

/** An object a dataflow maintains. */
export interface DataflowExportRow {
  objectId: string;
  dataflowId: string;
  /** The connection id of a subscribe's session, or null for other exports. */
  connectionId: number | null;
  /** The dataflow's name, for a transient (`t`) export. */
  dataflowName: string | null;
}

/** A row of `buildWorkerCpuSubscribe`. */
export type WorkerCpuRow =
  | ({ kind: "elapsed" } & DataflowWorkerCpuRow & {
        objectId: null;
        connectionId: null;
        dataflowName: null;
      })
  | ({ kind: "export" } & DataflowExportRow & {
        workerId: null;
        elapsedNs: null;
      });

/** Each dataflow's cumulative time on each worker, in 10 ms units. */
export function buildDataflowElapsedQuery() {
  // NOTE: roots are filtered before counting, joined on the full key of both
  // relations' indexes. Counting through `mz_scheduling_elapsed_per_worker`
  // keeps a count per operator and worker in the subscribe's dataflow:
  // measured 94 against 13 ms/s of CPU per worker, and 92 against 0.7 MB, on
  // 500 dataflows over 8 workers.
  return queryBuilder
    .selectFrom("mz_scheduling_elapsed_raw as r")
    .innerJoin("mz_dataflow_addresses_per_worker as a", (join) =>
      join.onRef("a.id", "=", "r.id").onRef("a.worker_id", "=", "r.worker_id"),
    )
    .where(isRootOperator)
    .groupBy([dataflowIdOfRoot, "r.worker_id"])
    .select([
      sql<WorkerCpuRow["kind"]>`'elapsed'`.as("kind"),
      sql<string>`${dataflowIdOfRoot}::text`.as("dataflowId"),
      sql<number | null>`${sql.ref("r.worker_id")}::int4`.as("workerId"),
      roundedElapsedNs(sql`count(*)`).as("elapsedNs"),
      sql<string | null>`NULL::text`.as("objectId"),
      sql<number | null>`NULL::uint4`.as("connectionId"),
      sql<string | null>`NULL::text`.as("dataflowName"),
    ]);
}

/**
 * What each dataflow maintains: user objects (`u` ids) and transient
 * dataflows (`t` ids), named so a caller can tell a session's queries and
 * subscribes from the coordinator's own. System dataflows still count toward
 * each worker's total, but have nothing to name.
 */
export function buildDataflowExportsQuery() {
  // NOTE: names are joined per worker, on the full key of the operators'
  // index. Through `mz_dataflows`, which keeps worker 0, the subscribe
  // arranges every operator's name.
  const transientNames = queryBuilder
    .selectFrom("mz_compute_exports_per_worker as xw")
    .innerJoin("mz_dataflow_addresses_per_worker as a", (join) =>
      join
        .onRef("a.worker_id", "=", "xw.worker_id")
        .on(sql<boolean>`${dataflowIdOfRoot} = ${sql.ref("xw.dataflow_id")}`)
        .on(isRootOperator),
    )
    .innerJoin("mz_dataflow_operators_per_worker as o", (join) =>
      join.onRef("o.id", "=", "a.id").onRef("o.worker_id", "=", "a.worker_id"),
    )
    .where("xw.export_id", "like", "t%")
    .select(["xw.export_id as exportId", "o.name"])
    .distinct();
  return queryBuilder
    .selectFrom("mz_compute_exports as x")
    .leftJoin("mz_subscriptions as s", "s.id", "x.export_id")
    .leftJoin("mz_sessions as ss", "ss.id", "s.session_id")
    .leftJoin(transientNames.as("n"), "n.exportId", "x.export_id")
    .where((eb) =>
      eb.or([eb("x.export_id", "like", "u%"), eb("x.export_id", "like", "t%")]),
    )
    .select([
      sql<WorkerCpuRow["kind"]>`'export'`.as("kind"),
      sql<string>`${sql.ref("x.dataflow_id")}::text`.as("dataflowId"),
      sql<number | null>`NULL::int4`.as("workerId"),
      sql<number | null>`NULL::float8`.as("elapsedNs"),
      sql<string | null>`${sql.ref("x.export_id")}`.as("objectId"),
      sql<number | null>`${sql.ref("ss.connection_id")}`.as("connectionId"),
      sql<string | null>`${sql.ref("n.name")}`.as("dataflowName"),
    ]);
}

/**
 * Each dataflow's time on each worker and what each dataflow maintains, in
 * one subscribe that ends on its own once its frontier passes `upToMs`, so a
 * client that stops reading without closing it stops costing the replica.
 */
export function buildWorkerCpuSubscribe(upToMs: number) {
  return buildSubscribeQuery<WorkerCpuRow>(
    buildDataflowElapsedQuery().unionAll(buildDataflowExportsQuery()),
    {
      upsertKey: ["kind", "dataflowId", "workerId", "objectId"],
      upToMs,
    },
  );
}

/** A `buildWorkerCpuSubscribe` row's upsert key. */
export const workerCpuRowKey = (row: WorkerCpuRow) =>
  `${row.kind}|${row.dataflowId}|${row.workerId}|${row.objectId}`;

/** `buildWorkerCpuSubscribe`'s rows, split by kind. */
export function splitWorkerCpuRows(rows: WorkerCpuRow[]) {
  const dataflows: DataflowWorkerCpuRow[] = [];
  const exports: DataflowExportRow[] = [];
  for (const row of rows) {
    if (row.kind === "elapsed") {
      const { dataflowId, workerId, elapsedNs } = row;
      dataflows.push({ dataflowId, workerId, elapsedNs });
    } else {
      const { objectId, dataflowId, connectionId, dataflowName } = row;
      exports.push({ objectId, dataflowId, connectionId, dataflowName });
    }
  }
  return { dataflows, exports };
}
